<template>
	<div class="notification-container">
		<!-- Single toast display with cycling -->
		<v-snackbar
			v-if="currentNotification"
			:model-value="true"
			:color="getToastColor(currentNotification.type)"
			:timeout="4000"
			location="top"
			class="toast-notification"
			elevation="6"
			rounded="lg"
			@update:model-value="onSnackbarClose"
		>
			<template #default>
				<div class="d-flex align-center justify-space-between w-100">
					<div class="d-flex align-center">
						<v-icon
							:icon="getAlertIcon(currentNotification.type)"
							class="mr-3"
							size="20"
						/>
						<div>
							<div class="toast-message">{{ currentNotification.message }}</div>
							<div class="toast-time">
								{{ formatTime(currentNotification.timestamp) }}
							</div>
						</div>
					</div>
				</div>
			</template>

			<template #actions>
				<v-btn
					icon="mdi-close"
					size="small"
					variant="text"
					@click="dismissCurrentNotification"
				/>
			</template>
		</v-snackbar>

		<!-- Notification queue indicator - moved to bottom -->
		<v-chip
			v-if="notifications.length > 1"
			color="info"
			size="small"
			class="notification-queue-indicator"
		>
			{{ notifications.length - 1 }} more
		</v-chip>
	</div>
</template>

<script setup>
import { useOfflineNotifications } from "~/composables/pwa/useOfflineNotifications"
import moment from "moment"

const {
	isOnline,
	queuedReports,
	notifications,
	removeNotification,
	requestNotificationPermission,
} = useOfflineNotifications()

// Current notification being displayed
const currentNotification = computed(() => {
	return notifications.value.length > 0 ? notifications.value[0] : null
})

// Auto-cycle through notifications
watch(currentNotification, (newNotification) => {
	if (newNotification) {
		console.log("Displaying notification:", newNotification)
		// Auto-dismiss after 4 seconds if user doesn't interact
		setTimeout(() => {
			if (currentNotification.value?.id === newNotification.id) {
				dismissCurrentNotification()
			}
		}, 4000)
	}
})

onMounted(() => {
	console.log("OfflineNotifications component mounted")
	requestNotificationPermission()
})

const onSnackbarClose = (value) => {
	if (!value) {
		dismissCurrentNotification()
	}
}

const dismissCurrentNotification = () => {
	if (currentNotification.value) {
		removeNotification(currentNotification.value.id)
	}
}

const getToastColor = (type) => {
	const colorMap = {
		success: "success",
		error: "error",
		warning: "warning",
		info: "info",
	}
	return colorMap[type] || "info"
}

const getAlertIcon = (type) => {
	const iconMap = {
		success: "mdi-check-circle",
		error: "mdi-alert-circle",
		warning: "mdi-alert",
		info: "mdi-information",
	}
	return iconMap[type] || "mdi-information"
}

const formatTime = (timestamp) => {
	return moment(timestamp).format("HH:mm")
}
</script>

<style scoped>
.notification-container {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 9999;
	pointer-events: none;
}

.toast-notification {
	pointer-events: auto;
}

.notification-queue-indicator {
	position: fixed;
	top: 80px;
	right: 20px;
	z-index: 10001;
	pointer-events: auto;
}

.toast-message {
	font-weight: 500;
	line-height: 1.4;
}

.toast-time {
	font-size: 12px;
	opacity: 0.7;
	margin-top: 2px;
}

@media (max-width: 600px) {
	.notification-queue-indicator {
		right: 10px;
	}
}
</style>
